import { useQuery } from '@tanstack/react-query';
import { GlobalApi } from '@Apis';

/**
 * Interface for permissions response from API
 */
interface PermissionsResponse {
  permissions: {
    [key: string]: string[];
  };
  orgIds: string;
}

/**
 * Validates if all permissions have both "read" and "write" access
 *
 * @param permissions - Object with permission keys and their access arrays
 * @returns true if all permissions have both "read" and "write", false otherwise
 *
 * @example
 * validateAllPermissions({
 *   "administrator": ["read", "write"],
 *   "dashboard": ["read", "write"]
 * }) // returns true
 *
 * validateAllPermissions({
 *   "administrator": ["read"],
 *   "dashboard": ["read", "write"]
 * }) // returns false
 */
export const validateAllPermissions = (
  permissionsData: PermissionsResponse
): boolean => {
  return (
    permissionsData.orgIds === 'all' &&
    Object.values(permissionsData.permissions).every(
      (permArray) =>
        Array.isArray(permArray) &&
        permArray.includes('read') &&
        permArray.includes('write')
    )
  );
};

/**
 * Custom hook to check user permissions using TanStack Query
 * Caches results for 5 minutes to reduce API calls
 *
 * @returns Query result with hasFullPermissions boolean and loading/error states
 */
export const useGlobalPermissions = () => {
  return useQuery<boolean>({
    queryKey: ['globalPermissions'],
    queryFn: async () => {
      try {
        const response = (await GlobalApi.getPermissions()) as any;
        const permissionsData: PermissionsResponse = response.data;

        if (!permissionsData?.permissions || !permissionsData.orgIds) {
          return false;
        }

        return validateAllPermissions(permissionsData);
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
        throw error;
      }
    },
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Retry once on failure
    retry: 1,
    // Don't refetch on window focus to avoid unnecessary API calls
    refetchOnWindowFocus: false,
  });
};
